/**
 * v2 accounts hub — primary banner header, avatar, and 2-column option grid.
 */
import React, { useMemo } from 'react';
import { useAuthNavigation } from '../../../shared/navigation/useAuthNavigation';
import { View, Text, StyleSheet, Linking, ScrollView, Image } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIcon } from '../../../components/common/SvgIcon';
import { Clickable } from '../../../components/common/Clickable';
import { useAuthStore } from '../../../shared/auth/store';
import { logout } from '../../../shared/auth';
import { useTranslation } from '../../../shared/i18n';
import { appTheme, useThemeColors, type ThemeColors } from '../../../shared/theme';
import { AnimatedSection } from '../../../components/common/AnimatedSection';
import { isDevHubClient } from '../../../shared/config/applications';
import {
  useOnlineOrdersFeatureEnabled,
  useCommunityFeatureEnabled,
  useConsultationsFeatureEnabled,
  useApiConfig,
  resolveOrganizationSocialLinks,
} from '../../../data/domains/config';
import {
  formatMealDaysRemainingLabel,
  getActiveMealDayProgressFromSubscription,
  useSubscriptionContext,
} from '../../../data/domains/subscription';

const AVATAR_SIZE = 60;
const BANNER_BLOCK_HEIGHT = 100;
const GRID_GAP = appTheme.spacing.md ?? 12;
const CONTENT_PADDING_H = appTheme.spacing.md ?? 12;

interface GridOptionCardProps {
  icon: string;
  title: string;
  subtitle: string;
  onPress: () => void;
  colors: ThemeColors;
}

function makeGridOptionStyles(colors: ThemeColors) {
  return StyleSheet.create({
    gridCard: {
      flex: 1,
      minWidth: 0,
      minHeight: 120,
      width: '100%',
      backgroundColor: colors.surface,
      borderRadius: 18,
      alignSelf: 'stretch',
      alignItems: 'center',
      justifyContent: 'flex-start',
      paddingTop: 10,
      paddingBottom: 8,
      shadowColor: colors.shadowColor,
      ...appTheme.shadows.xs,
    },
    card: {
      width: '100%',
      paddingHorizontal: 4,
      backgroundColor: 'transparent',
      borderRadius: 18,
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    iconCircle: {
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: colors.surfaceElevated,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 6,
    },
    label: {
      ...appTheme.typography.bodySmall,
      fontSize: 13,
      lineHeight: 18,
      fontWeight: '700',
      color: colors.text,
      textAlign: 'center',
      width: '100%',
    },
    subtitle: {
      ...appTheme.typography.captionSmall,
      fontSize: 11,
      lineHeight: 15,
      fontWeight: '400',
      color: colors.textSecondary,
      textAlign: 'center',
      width: '100%',
      marginTop: 2,
    },
  });
}

function createAccountsScreenStyles(c: ThemeColors) {
  return StyleSheet.create({
    screenRoot: {
      flex: 1,
      backgroundColor: c.background,
    },
    scroll: {
      flex: 1,
      zIndex: 1,
      backgroundColor: c.background,
    },
    scrollContent: {
      paddingHorizontal: CONTENT_PADDING_H,
      backgroundColor: c.background,
    },
    mainHeader: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      zIndex: 10,
      ...appTheme.shadows.xs,
      backgroundColor: c.primary,
      overflow: 'hidden',
      borderBottomLeftRadius: appTheme.borderRadius.xl * 2,
      borderBottomRightRadius: appTheme.borderRadius.xl * 2,
    },
    mainHeaderContent: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'flex-start',
      zIndex: 100,
      paddingHorizontal: appTheme.spacing.lg,
      paddingBottom: appTheme.spacing.lg,
      backgroundColor: 'transparent',
      flexDirection: 'row',
    },
    avatarWrap: {
      width: AVATAR_SIZE + 3,
      position: 'relative',
      marginBottom: appTheme.spacing.md,
    },
    userNameWrap: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    avatarImage: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      borderRadius: AVATAR_SIZE / 2,
      borderWidth: 2,
      borderColor: c.onPrimary,
    },
    avatarPlaceholder: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      borderRadius: AVATAR_SIZE / 2,
      borderWidth: 2,
      borderColor: c.onPrimary,
      backgroundColor: c.primaryUltra,
      alignItems: 'center',
      justifyContent: 'center',
    },
    editIconWrap: {
      position: 'absolute',
      bottom: 0,
      end: -2,
      width: 25,
      height: 25,
      borderRadius: 15,
      backgroundColor: c.onPrimary,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1.5,
      borderColor: c.onPrimary,
      ...appTheme.shadows.sm,
    },
    editIconWrapContent: {
      padding: 2.5,
      width: 20,
      height: 20,
      borderRadius: 13,
      backgroundColor: c.onPrimary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    userName: {
      ...appTheme.typography.title,
      fontSize: 20,
      fontWeight: '700',
      color: c.onPrimary,
      marginBottom: appTheme.spacing.xxs,
      textTransform: 'capitalize',
      width: '100%',
      textAlign: 'center',
    },
    userMeta: {
      ...appTheme.typography.bodySmall,
      fontSize: 14,
      lineHeight: 20,
      color: c.onPrimary,
      opacity: 0.92,
    },
    gridWrap: {
      flexDirection: 'column',
      marginBottom: appTheme.spacing.lg,
    },
    gridRow: {
      flexDirection: 'row',
      gap: GRID_GAP,
    },
    gridRowSpaced: {
      marginTop: GRID_GAP,
    },
    gridCell: {
      flex: 1,
      minWidth: 0,
    },
    socialRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      gap: appTheme.spacing.lg,
      paddingVertical: appTheme.spacing.lg,
    },
    socialBtn: {
      width: 52,
      height: 52,
      borderRadius: 12,
      backgroundColor: c.surface,
      alignItems: 'center',
      justifyContent: 'center',
      shadowColor: c.shadowColor,
      ...appTheme.shadows.xs,
    },
    logoutButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: appTheme.spacing.sm ?? 8,
      marginTop: appTheme.spacing.lg ?? 16,
      marginBottom: appTheme.spacing.md ?? 12,
      paddingVertical: appTheme.spacing.md ?? 12,
      paddingHorizontal: appTheme.spacing.lg ?? 16,
      borderRadius: appTheme.borderRadius?.md ?? 12,
    },
    logoutButtonText: {
      ...appTheme.typography.bodyMedium,
      fontWeight: '600',
      textDecorationLine: 'underline',
    },
    footer: {
      alignItems: 'center',
      paddingVertical: appTheme.spacing.md,
    },
    footerText: {
      ...appTheme.typography.captionSmall,
      color: c.textSecondary,
    },
  });
}

function GridOptionCard({ icon, title, subtitle, onPress, colors }: GridOptionCardProps) {
  const g = useMemo(() => makeGridOptionStyles(colors), [colors]);
  const a11y = `${title}. ${subtitle}`;
  return (
    <Clickable onPress={onPress} style={g.gridCard} accessibilityLabel={a11y} accessibilityRole="button">
      <View style={g.card}>
        <View style={g.iconCircle}>
          <SvgIcon name={icon} size={26} color={colors.primary} />
        </View>
        <Text style={g.label} numberOfLines={2} ellipsizeMode="tail">
          {title}
        </Text>
        <Text style={g.subtitle} numberOfLines={2} ellipsizeMode="tail">
          {subtitle}
        </Text>
      </View>
    </Clickable>
  );
}

export const AccountsScreenSet2: React.FC = () => {
  const navigation = useAuthNavigation();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const theme_colors = useThemeColors() ?? appTheme.colors;
  const user = useAuthStore(s => s.user);
  const { organization } = useApiConfig();
  const social_links = useMemo(() => resolveOrganizationSocialLinks(), [organization]);
  const { activeSubscriptions, availableDates, subscriptions, dayConfigMap, config } = useSubscriptionContext();
  const active_sub = activeSubscriptions[0];
  const meal_progress = useMemo(
    () =>
      active_sub
        ? getActiveMealDayProgressFromSubscription(active_sub, availableDates, subscriptions, dayConfigMap, config)
        : null,
    [active_sub, availableDates, subscriptions, dayConfigMap, config],
  );
  const subscriptions_subtitle = active_sub
    ? formatMealDaysRemainingLabel(meal_progress?.daysLeft ?? 0, meal_progress?.pausedDaysLeft ?? 0, t)
    : t('account.manageYourPlans');

  const handleLogout = async () => {
    await logout();
  };

  const openSocialLink = (url: string) => {
    Linking.openURL(url).catch(() => {});
  };

  const padding_bottom = insets.bottom + appTheme.spacing.lg;
  const styles = useMemo(() => createAccountsScreenStyles(theme_colors), [theme_colors]);
  const show_online_orders = useOnlineOrdersFeatureEnabled();
  const show_community = useCommunityFeatureEnabled();
  const show_consultations = useConsultationsFeatureEnabled();
  const display_name =
    user?.name || user?.first_name || user?.last_name
      ? [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.name
      : t('account.guest');
  const display_email = user?.mail ?? '';
  const display_number = user?.phone ?? '';
  const banner_block_height = insets.top + BANNER_BLOCK_HEIGHT;
  const content_top = banner_block_height + (appTheme.spacing.xl ?? 24);
  const nav = (route: string) => (navigation as { navigate: (n: string) => void }).navigate(route);

  return (
    <View style={styles.screenRoot}>
      <View pointerEvents="none" style={[styles.mainHeader, { height: banner_block_height }]} collapsable={false}>
        <View style={[styles.mainHeaderContent, { paddingTop: insets.top + appTheme.spacing.lg }]}>
          <View style={styles.avatarWrap}>
            {user?.avatar ? (
              <Image source={{ uri: user.avatar }} style={styles.avatarImage} />
            ) : (
              <View style={styles.avatarPlaceholder}>
                <SvgIcon name="user" size={AVATAR_SIZE * 0.5} color={theme_colors.onPrimary} />
              </View>
            )}
            <Clickable
              style={styles.editIconWrap}
              onPress={() => nav('Profile')}
              accessibilityLabel={t('account.editProfile')}
              accessibilityRole="button">
              <View style={styles.editIconWrapContent}>
                <SvgIcon name="edit" size={18} color={theme_colors.primary} />
              </View>
            </Clickable>
          </View>
          <View style={styles.userNameWrap}>
            <Text style={styles.userName} numberOfLines={1}>
              {display_name}
            </Text>
            <Text style={styles.userMeta} numberOfLines={1}>
              {display_number}
            </Text>
            <Text style={styles.userMeta} numberOfLines={1}>
              {display_email}
            </Text>
          </View>
          <View style={styles.avatarWrap} />
        </View>
      </View>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.scrollContent, { paddingTop: content_top, paddingBottom: padding_bottom }]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled">
        <View style={styles.gridWrap}>
          <View style={styles.gridRow}>
            <View style={styles.gridCell}>
              <GridOptionCard
                colors={theme_colors}
                icon="user"
                title={t('account.profile')}
                subtitle={t('account.editYourInformation')}
                onPress={() => nav('Profile')}
              />
            </View>
            <View style={styles.gridCell}>
              <GridOptionCard
                colors={theme_colors}
                icon="map"
                title={t('account.addresses')}
                subtitle={t('account.manageDeliveryAddresses')}
                onPress={() => nav('AddressLists')}
              />
            </View>
          </View>
          <View style={[styles.gridRow, styles.gridRowSpaced]}>
            <View style={styles.gridCell}>
              <GridOptionCard
                colors={theme_colors}
                icon="meal"
                title={t('account.mySubscriptions')}
                subtitle={subscriptions_subtitle}
                onPress={() => nav('MySubscriptions')}
              />
            </View>
            <View style={styles.gridCell}>
              <GridOptionCard
                colors={theme_colors}
                icon="flame"
                title={t('account.allergiesAndDislikes')}
                subtitle={t('account.manageAllergiesDislikes')}
                onPress={() => nav('AllergiesDislikes')}
              />
            </View>
          </View>
          <View style={[styles.gridRow, styles.gridRowSpaced]}>
            {show_online_orders ? (
              <View style={styles.gridCell}>
                <GridOptionCard
                  colors={theme_colors}
                  icon="time"
                  title={t('account.orderHistory')}
                  subtitle={t('account.viewPastOrders')}
                  onPress={() => nav('OrderHistory')}
                />
              </View>
            ) : null}
            <View style={[styles.gridCell, !show_online_orders ? { flex: 1 } : undefined]}>
              <GridOptionCard
                colors={theme_colors}
                icon="settings"
                title={t('account.settings')}
                subtitle={t('account.languageAndPreferences')}
                onPress={() => nav('Settings')}
              />
            </View>
          </View>
          {isDevHubClient() ? (
            <View style={[styles.gridRow, styles.gridRowSpaced]}>
              <View style={styles.gridCell}>
                <GridOptionCard
                  colors={theme_colors}
                  icon="settings"
                  title="Dev Tools"
                  subtitle="UI tier, API target, perf"
                  onPress={() => nav('DevTools')}
                />
              </View>
            </View>
          ) : null}
          {show_consultations || show_community ? (
            <View style={[styles.gridRow, styles.gridRowSpaced]}>
              {show_consultations ? (
                <View style={styles.gridCell}>
                  <GridOptionCard
                    colors={theme_colors}
                    icon="chat"
                    title={t('account.consultation')}
                    subtitle={t('account.bookASession')}
                    onPress={() => nav('Consultation')}
                  />
                </View>
              ) : null}
              {show_community ? (
                <View style={[styles.gridCell, !show_consultations ? { flex: 1 } : undefined]}>
                  <GridOptionCard
                    colors={theme_colors}
                    icon="users"
                    title={t('account.community')}
                    subtitle={t('account.communitySubtitle')}
                    onPress={() => nav('Community')}
                  />
                </View>
              ) : null}
            </View>
          ) : null}
        </View>

        {social_links.length > 0 ? (
          <AnimatedSection index={0} use_fade_only>
            <View style={styles.socialRow}>
              {social_links.map(link => (
                <Clickable
                  key={link.key}
                  onPress={() => openSocialLink(link.url)}
                  accessibilityLabel={link.label}
                  accessibilityRole="link">
                  <View style={styles.socialBtn}>
                    <SvgIcon
                      name={link.icon}
                      size={24}
                      color={link.key === 'whatsapp' ? '#25D366' : link.key === 'instagram' ? '#E4405F' : '#FF0000'}
                    />
                  </View>
                </Clickable>
              ))}
            </View>
          </AnimatedSection>
        ) : null}

        <Clickable onPress={handleLogout} accessibilityLabel={t('account.logOut')} accessibilityRole="button">
          <View style={styles.logoutButton}>
            <SvgIcon name="logout" size={22} color={theme_colors.primary} />
            <Text style={[styles.logoutButtonText, { color: theme_colors.primary }]}>{t('account.logOut')}</Text>
          </View>
        </Clickable>

        <View style={[styles.footer, { paddingBottom: insets.bottom + appTheme.spacing.sm }]}>
          <Text style={[styles.footerText, { color: theme_colors.textLight }]}>{t('account.version')} 1.0.0</Text>
        </View>
      </ScrollView>
    </View>
  );
};

export default AccountsScreenSet2;
